import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Download, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { eur } from './PortalQuoteSections';

const BRAND = '#500000';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('nl-BE') : '-');

const StatusBadge = ({ inv }) => {
  if (inv.payment_status === 'paid') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Betaald</span>;
  if (inv.days_overdue > 0) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Vervallen · {inv.days_overdue} d</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Openstaand</span>;
};

const SummaryTile = ({ label, value, testId, accent }) => (
  <div className="rounded-lg bg-white border p-3 sm:p-4" data-testid={testId}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-base sm:text-lg font-bold mt-1" style={{ color: accent || '#1F2937' }}>{eur(value)}</p>
  </div>
);

const InvoiceItem = ({ inv, accessToken }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/customer-portal/${accessToken}/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Factuur kon niet gedownload worden'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`portal-invoice-${inv.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white rounded-lg border">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-sm text-gray-900">{inv.invoice_number}</p>
          <StatusBadge inv={inv} />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {inv.label}{inv.percentage && inv.quote_id ? ` · ${Number(inv.percentage).toLocaleString('nl-BE')}% van offerte ${inv.quote_id}` : ''}
        </p>
        <p className="text-xs text-gray-500">Factuurdatum {fmtDate(inv.invoice_date)} · vervaldatum {fmtDate(inv.due_date)}</p>
        {inv.payment_status !== 'paid' && inv.payment_reference && (
          <p className="text-xs mt-1 text-gray-700">Mededeling: <span className="font-mono font-semibold" data-testid={`portal-invoice-ogm-${inv.id}`}>{inv.payment_reference}</span></p>
        )}
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-3">
        <span className="font-bold text-base whitespace-nowrap" data-testid={`portal-invoice-amount-${inv.id}`}>{eur(inv.total_incl_vat)}</span>
        <button type="button" onClick={download} disabled={busy} data-testid={`portal-invoice-pdf-${inv.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: BRAND }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}PDF
        </button>
      </div>
    </div>
  );
};

export const PortalInvoices = ({ invoices = [], summary, accessToken }) => {
  if (!invoices.length) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid="portal-invoices-empty">
        <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">Er zijn nog geen facturen voor dit project.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="portal-invoices">
      <div className={`grid grid-cols-2 ${invoices.some((i) => i.quote_id) ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
        <SummaryTile label="Nog te betalen" value={summary.open_amount} testId="portal-invoices-open" accent={BRAND} />
        <SummaryTile label="Waarvan vervallen" value={summary.overdue_amount} testId="portal-invoices-overdue" accent={summary.overdue_amount > 0 ? '#B91C1C' : undefined} />
        <SummaryTile label="Al betaald" value={summary.total_paid} testId="portal-invoices-paid" accent="#15803D" />
        {invoices.some((i) => i.quote_id) && <SummaryTile label="Nog te factureren" value={summary.not_yet_invoiced} testId="portal-invoices-not-invoiced" />}
      </div>
      <div className="space-y-2">
        {invoices.map((inv) => <InvoiceItem key={inv.id} inv={inv} accessToken={accessToken} />)}
      </div>
    </div>
  );
};
